import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { PostsPanel } from '../../components/community/PostsPanel';
import { CommunityShell, NoticeCard } from '../../components/community/Shell';
import { GameIcon } from '../../components/games/GameArt';
import { launchFromQuery } from '../../components/play/launch';
import { PlayScreen, pillClass } from '../../components/play/PlayScreen';
import { PageSpinner } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { type TestCommunity as Community, fetchTestCommunity } from '../../lib/communities';
import { useSession } from '../../lib/session';
import { PrivateTest } from './TestCommunity';

type State =
  | { status: 'loading' }
  | { status: 'private' }
  | { status: 'failed' }
  | { status: 'ready'; data: Community };

/** mogzo.com/test/<address>/play: a launched game on a computer, with the community beside it. */
export function PlayPage() {
  const { slug = '' } = useParams();
  const [params] = useSearchParams();
  const buildNumber = Number(params.get('build')) || undefined;
  const { me } = useSession();
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    if (me === undefined) return;
    if (me === null) {
      setState({ status: 'private' });
      return;
    }
    let active = true;
    fetchTestCommunity(slug, buildNumber)
      .then((data) => active && setState({ status: 'ready', data }))
      .catch((error) => {
        if (!active) return;
        const hidden = error instanceof ApiError && [401, 403, 404].includes(error.status);
        setState({ status: hidden ? 'private' : 'failed' });
      });
    return () => {
      active = false;
    };
  }, [slug, buildNumber, me]);

  if (state.status === 'loading') return <PageSpinner />;
  if (state.status === 'ready') {
    return (
      <Player
        data={state.data}
        launch={params.get('launch')}
        invite={params.get('match')}
        openMatch={params.get('open')}
        key={state.data.build?.number}
      />
    );
  }
  return (
    <CommunityShell>
      {state.status === 'private' && <PrivateTest next={`/test/${slug}`} />}
      {state.status === 'failed' && (
        <NoticeCard title="Couldn’t load this page">
          <p>MOGZO didn’t answer. Reload the page to try again.</p>
        </NoticeCard>
      )}
    </CommunityShell>
  );
}

/** The build being tested, with the test's posts beside it: the same play page as a live game. */
function Player({
  data,
  launch,
  invite,
  openMatch,
}: {
  data: Community;
  launch: string | null;
  invite: string | null;
  openMatch: string | null;
}) {
  const { community, build, viewer } = data;
  const home = `/test/${community.slug}`;

  if (!build) {
    return (
      <CommunityShell>
        <NoticeCard title="Nothing to play yet">
          <p>
            This test has no build to play. <Link to={home}>Back to {community.name}</Link>
          </p>
        </NoticeCard>
      </CommunityShell>
    );
  }

  return (
    <PlayScreen
      name={community.name}
      playUrl={build.playUrl}
      projectId={community.projectId}
      portrait={build.orientation === 'portrait'}
      launch={launchFromQuery(launch)}
      invite={invite}
      openMatch={openMatch}
      isDeveloper={viewer.role === 'owner'}
      home={home}
      side={(back) => (
        <PostsPanel
          back={back}
          communityId={community.id}
          path={home}
          posting="open"
          placeholder="Share something with the testers…"
        />
      )}
    >
      <div className="flex shrink-0 flex-wrap items-center gap-x-3.5 gap-y-2.5">
        <Link to={home} aria-label={`${community.name}’s test community`}>
          <GameIcon
            game={{ name: community.name, iconUrl: null }}
            className="size-12 rounded-[13px] text-[17px]"
          />
        </Link>
        <div className="min-w-0 flex-[1_1_220px]">
          <h1 className="truncate text-xl leading-[26px] font-bold tracking-[-0.01em]">
            {community.name}
          </h1>
          <p className="text-sm text-muted">
            by {community.developerName} · private test · build #{build.number}
          </p>
        </div>
        <Link to={home} className={pillClass}>
          Test community
        </Link>
      </div>
    </PlayScreen>
  );
}
